import { useState } from 'react'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Slider } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import { Upload, Zap, FileText, Users, X, Globe, Sword, Lock, Unlock } from 'lucide-react'
import type { EnemyInput, EnemyResult, EnemyLockedSections } from '@creanizer/types'
import { generateWorldContextText, generateEnemyContext } from '@creanizer/utils'
import { usePlotStore } from '../../../../stores/plotStore'
import { useCharacterStore } from '../../../../stores/characterStore'
import { useWorldBuilderStore } from '../../../../stores/worldbuilderStore'
import { useEnemyStore } from '../../../../stores/enemyStore'

interface EnemyFormProps {
  input: EnemyInput
  onChange: (input: EnemyInput) => void
  onGenerate: () => void
  isGenerating: boolean
  useStreaming?: boolean
  onStreamingChange?: (value: boolean) => void
  partialRegenerateMode?: boolean
  existingResult?: EnemyResult | null
  lockedSections?: EnemyLockedSections
  onLockedSectionsChange?: (sections: EnemyLockedSections) => void
  onPartialRegenerateModeChange?: (mode: boolean) => void
}

export function EnemyForm({ 
  input, 
  onChange, 
  onGenerate, 
  isGenerating, 
  useStreaming = false, 
  onStreamingChange,
  partialRegenerateMode = false,
  existingResult = null,
  lockedSections = {},
  onLockedSectionsChange,
  onPartialRegenerateModeChange
}: EnemyFormProps) {
  const [fileContent, setFileContent] = useState<string>('')
  const [selectedPlotId, setSelectedPlotId] = useState<string>('')
  const [selectedCharacterIds, setSelectedCharacterIds] = useState<string[]>([])
  const [selectedWorldId, setSelectedWorldId] = useState<string>('')
  const [selectedEnemyIds, setSelectedEnemyIds] = useState<string[]>([])
  
  const { savedPlots } = usePlotStore()
  const { characters } = useCharacterStore()
  const { savedWorlds } = useWorldBuilderStore()
  const { enemies } = useEnemyStore()

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        const content = e.target?.result as string
        setFileContent(content)
        onChange({ ...input, uploadedContent: content })
      }
      reader.readAsText(file)
    }
  }

  const handlePlotSelect = (plotId: string) => {
    setSelectedPlotId(plotId)
    const plot = savedPlots.find(p => p.id === plotId)
    if (!plot) return

    const plotContext = `
タイトル: ${plot.result.title}
テーマ: ${plot.result.themes.join('、')}
あらすじ: ${plot.result.synopsis}
主要キャラクター:
${plot.result.characters.map(c => `- ${c.name}: ${c.role} - ${c.description || ''}`).join('\n')}
`.trim()
    
    onChange({ ...input, plotContext })
  }

  const handleCharacterToggle = (characterId: string) => {
    const newSelection = selectedCharacterIds.includes(characterId)
      ? selectedCharacterIds.filter(id => id !== characterId)
      : [...selectedCharacterIds, characterId]
    
    setSelectedCharacterIds(newSelection)
    
    const selectedChars = newSelection.map(id => 
      characters.find(c => c.id === id)
    ).filter(Boolean)
    
    if (selectedChars.length > 0) {
      const characterContext = selectedChars.map(char => {
        if (!char) return ''
        const c = char.result
        return `
【${c.name}】
年齢: ${c.age || '不明'}
性別: ${c.gender || '不明'}
役割: ${c.role || '不明'}
概要: ${c.summary || ''}
性格: ${c.personality?.traits?.join('、') || ''}
`.trim()
      }).join('\n\n')
      
      onChange({ ...input, characterContext })
    } else {
      onChange({ ...input, characterContext: undefined })
    }
  }

  const handleEnemyToggle = (enemyId: string) => {
    const newSelection = selectedEnemyIds.includes(enemyId)
      ? selectedEnemyIds.filter(id => id !== enemyId)
      : [...selectedEnemyIds, enemyId]
    
    setSelectedEnemyIds(newSelection)
    
    const selectedEnemies = newSelection.map(id => 
      enemies.find(e => e.id === id)
    ).filter(Boolean)
    
    if (selectedEnemies.length > 0) {
      const enemyContexts = selectedEnemies.map(enemy => {
        if (!enemy) return ''
        return generateEnemyContext(enemy)
      }).filter(Boolean).join('\n\n')
      
      // relatedEnemiesをIDリストとして設定し、詳細はrelatedEnemiesContextに設定
      onChange({ 
        ...input, 
        relatedEnemies: newSelection,
        relatedEnemiesContext: enemyContexts
      })
    } else {
      // 関連エネミーの選択を解除した場合
      onChange({ 
        ...input, 
        relatedEnemies: undefined,
        relatedEnemiesContext: undefined
      })
    }
  }

  // モード変更時のハンドラ - 他のモードの入力をクリア
  const handleModeChange = (newMode: EnemyInput['mode']) => {
    // モード固有のフィールドをリセット
    if (newMode === 'standard') {
      onChange({
        mode: newMode,
        creativityLevel: input.creativityLevel,
        seed: input.seed,
        useProMode: input.useProMode
      })
      // アイディアモードの選択をクリア
      setSelectedPlotId('')
      setSelectedCharacterIds([])
      setSelectedEnemyIds([])
      setFileContent('')
    } else if (newMode === 'idea') {
      onChange({
        mode: newMode,
        ideaText: input.ideaText,
        uploadedContent: input.uploadedContent,
        plotContext: input.plotContext,
        characterContext: input.characterContext,
        creativityLevel: input.creativityLevel,
        seed: input.seed,
        useProMode: input.useProMode
      })
    } else if (newMode === 'batch') {
      onChange({
        mode: newMode,
        numberOfEnemies: input.numberOfEnemies || 3,
        creativityLevel: input.creativityLevel,
        seed: input.seed,
        useProMode: input.useProMode
      })
      // アイディアモードの選択をクリア
      setSelectedPlotId('')
      setSelectedCharacterIds([])
      setSelectedEnemyIds([])
      setFileContent('')
    }
  }

  return (
    <div className="space-y-6">
      {/* 部分再生成モード */}
      {partialRegenerateMode && existingResult && (
        <div className="space-y-4 border rounded-lg p-4 bg-muted/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4" />
              <h3 className="font-semibold">部分再生成設定</h3>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  if (onLockedSectionsChange) {
                    // 変更不可の項目以外をすべてロック
                    onLockedSectionsChange({
                      basicInfo: {
                        name: true,
                        title: true,
                        aliases: true,
                        type: true,
                        category: true,
                        threat: true,
                        classification: true,
                        summary: true,
                        description: true,
                        habitat: true,
                        distribution: true,
                        timeOfActivity: true,
                        frequency: true
                      },
                      appearance: true,
                      abilities: true,
                      behavior: true,
                      lore: true,
                      encounter: true,
                      gmNotes: true
                    })
                  }
                }}
              >
                一括ロック
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  if (onLockedSectionsChange) {
                    // すべてのロックを解除（変更不可の項目は除く）
                    onLockedSectionsChange({
                      basicInfo: {
                        name: true,
                        type: true,
                        category: true,
                        threat: true
                      }
                    })
                  }
                }}
              >
                一括解除
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => {
                  if (onPartialRegenerateModeChange) {
                    onPartialRegenerateModeChange(false)
                  }
                }}
              >
                <X className="h-3 w-3 mr-1" />
                部分再生成を終了
              </Button>
            </div>
          </div>
          
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-3 lg:grid-cols-6 gap-1 h-auto">
              <TabsTrigger value="basic" className="text-xs">基本</TabsTrigger>
              <TabsTrigger value="appearance" className="text-xs">外見</TabsTrigger>
              <TabsTrigger value="abilities" className="text-xs">能力</TabsTrigger>
              <TabsTrigger value="behavior" className="text-xs">行動</TabsTrigger>
              <TabsTrigger value="lore" className="text-xs">伝承</TabsTrigger>
              <TabsTrigger value="encounter" className="text-xs">遭遇</TabsTrigger>
            </TabsList>
            
            {/* 基本タブ */}
            <TabsContent value="basic" className="space-y-4">
              <div className="space-y-3">
                <div className="text-sm text-muted-foreground">
                  ※ 名前、タイプ、カテゴリー、脅威度は変更できません
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {[
                    { key: 'title', label: '称号', locked: lockedSections.basicInfo?.title, immutable: false },
                    { key: 'classification', label: '分類', locked: lockedSections.basicInfo?.classification, immutable: false },
                    { key: 'summary', label: '概要', locked: lockedSections.basicInfo?.summary, immutable: false },
                    { key: 'description', label: '詳細説明', locked: lockedSections.basicInfo?.description, immutable: false },
                    { key: 'habitat', label: '生息地', locked: lockedSections.basicInfo?.habitat, immutable: false },
                    { key: 'distribution', label: '分布', locked: lockedSections.basicInfo?.distribution, immutable: false },
                    { key: 'timeOfActivity', label: '活動時間', locked: lockedSections.basicInfo?.timeOfActivity, immutable: false },
                    { key: 'frequency', label: '遭遇頻度', locked: lockedSections.basicInfo?.frequency, immutable: false }
                  ].map(item => (
                    <Button
                      key={item.key}
                      variant={item.locked ? "default" : "outline"}
                      size="sm"
                      disabled={item.immutable}
                      onClick={() => {
                        if (onLockedSectionsChange && !item.immutable) {
                          onLockedSectionsChange({
                            ...lockedSections,
                            basicInfo: {
                              ...lockedSections.basicInfo,
                              name: true,
                              type: true,
                              category: true,
                              threat: true,
                              [item.key]: !item.locked
                            }
                          })
                        }
                      }}
                      className="justify-start"
                    >
                      {item.locked ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                      {item.label}
                    </Button>
                  ))}
                </div>
                
                {/* 別名の個別ロック */}
                {existingResult?.basicInfo?.aliases && existingResult.basicInfo.aliases.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium">別名</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      <Button
                        variant={lockedSections.basicInfo?.aliases === true ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          if (onLockedSectionsChange) {
                            onLockedSectionsChange({
                              ...lockedSections,
                              basicInfo: {
                                ...lockedSections.basicInfo,
                                name: true,
                                type: true,
                                category: true,
                                threat: true,
                                aliases: lockedSections.basicInfo?.aliases === true ? false : true
                              }
                            })
                          }
                        }}
                        className="justify-start"
                      >
                        {lockedSections.basicInfo?.aliases === true ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                        別名全体
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ml-4">
                      {existingResult.basicInfo.aliases.map((alias, index) => {
                        const isLocked = Array.isArray(lockedSections.basicInfo?.aliases) 
                          ? lockedSections.basicInfo.aliases.includes(index)
                          : lockedSections.basicInfo?.aliases === true
                        return (
                          <Button
                            key={index}
                            variant={isLocked ? "default" : "outline"}
                            size="sm"
                            onClick={() => {
                              if (onLockedSectionsChange) {
                                let newAliases: boolean | number[]
                                if (Array.isArray(lockedSections.basicInfo?.aliases)) {
                                  const current = lockedSections.basicInfo.aliases
                                  if (current.includes(index)) {
                                    newAliases = current.filter(i => i !== index)
                                  } else {
                                    newAliases = [...current, index]
                                  }
                                } else {
                                  newAliases = isLocked ? [] : [index]
                                }
                                onLockedSectionsChange({
                                  ...lockedSections,
                                  basicInfo: {
                                    ...lockedSections.basicInfo,
                                    name: true,
                                    type: true,
                                    category: true,
                                    threat: true,
                                    aliases: newAliases.length === 0 ? false : newAliases
                                  }
                                })
                              }
                            }}
                            className="justify-start text-left"
                          >
                            {isLocked ? <Lock className="h-3 w-3 mr-1 flex-shrink-0" /> : <Unlock className="h-3 w-3 mr-1 flex-shrink-0" />}
                            <span className="truncate">{alias}</span>
                          </Button>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
            
            {/* 外見タブ */}
            <TabsContent value="appearance" className="space-y-4">
              <div className="space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  <Button
                    variant={lockedSections.appearance === true ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      if (onLockedSectionsChange) {
                        onLockedSectionsChange({
                          ...lockedSections,
                          appearance: lockedSections.appearance === true ? false : true
                        })
                      }
                    }}
                    className="justify-start"
                  >
                    {lockedSections.appearance === true ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                    外見全体
                  </Button>
                </div>
                
                {/* 基本的な外見項目 */}
                <div className="space-y-2">
                  <div className="text-sm font-medium">基本的な外見</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 ml-4">
                    {[
                      { key: 'overallDescription', label: '全体的な外見' },
                      { key: 'size', label: 'サイズ' },
                      { key: 'height', label: '高さ' },
                      { key: 'weight', label: '重さ' },
                      { key: 'bodyType', label: '体型' },
                      { key: 'colorScheme', label: '色彩' },
                      { key: 'visualImpact', label: '視覚的インパクト' },
                      { key: 'firstImpressionFear', label: '初見の恐怖度' }
                    ].map(item => {
                      const isLocked = typeof lockedSections.appearance === 'object' 
                        ? lockedSections.appearance[item.key as keyof typeof lockedSections.appearance]
                        : lockedSections.appearance === true
                      return (
                        <Button
                          key={item.key}
                          variant={isLocked ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            if (onLockedSectionsChange) {
                              const currentAppearance = typeof lockedSections.appearance === 'object' 
                                ? lockedSections.appearance 
                                : {}
                              onLockedSectionsChange({
                                ...lockedSections,
                                appearance: {
                                  ...currentAppearance,
                                  [item.key]: !isLocked
                                }
                              })
                            }
                          }}
                          className="justify-start"
                        >
                          {isLocked ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                          {item.label}
                        </Button>
                      )
                    })}
                  </div>
                </div>
                
                {/* 特徴的な部位の個別ロック */}
                {existingResult?.appearance?.distinctiveFeatures && existingResult.appearance.distinctiveFeatures.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium">特徴的な部位</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      <Button
                        variant={
                          typeof lockedSections.appearance === 'object' && lockedSections.appearance?.distinctiveFeatures === true 
                            ? "default" 
                            : "outline"
                        }
                        size="sm"
                        onClick={() => {
                          if (onLockedSectionsChange) {
                            const currentAppearance = typeof lockedSections.appearance === 'object' ? lockedSections.appearance : {}
                            onLockedSectionsChange({
                              ...lockedSections,
                              appearance: {
                                ...currentAppearance,
                                distinctiveFeatures: currentAppearance.distinctiveFeatures === true ? false : true
                              }
                            })
                          }
                        }}
                        className="justify-start"
                      >
                        {typeof lockedSections.appearance === 'object' && lockedSections.appearance?.distinctiveFeatures === true 
                          ? <Lock className="h-3 w-3 mr-1" /> 
                          : <Unlock className="h-3 w-3 mr-1" />}
                        特徴全体
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ml-4">
                      {existingResult.appearance.distinctiveFeatures.map((feature, index) => {
                        const isLocked = typeof lockedSections.appearance === 'object' && (
                          Array.isArray(lockedSections.appearance.distinctiveFeatures) 
                            ? lockedSections.appearance.distinctiveFeatures.includes(index)
                            : lockedSections.appearance.distinctiveFeatures === true
                        )
                        return (
                          <Button
                            key={index}
                            variant={isLocked ? "default" : "outline"}
                            size="sm"
                            onClick={() => {
                              if (onLockedSectionsChange) {
                                const currentAppearance = typeof lockedSections.appearance === 'object' ? lockedSections.appearance : {}
                                let newFeatures: boolean | number[]
                                if (Array.isArray(currentAppearance.distinctiveFeatures)) {
                                  const current = currentAppearance.distinctiveFeatures
                                  if (current.includes(index)) {
                                    newFeatures = current.filter(i => i !== index)
                                  } else {
                                    newFeatures = [...current, index]
                                  }
                                } else {
                                  newFeatures = isLocked ? [] : [index]
                                }
                                onLockedSectionsChange({
                                  ...lockedSections,
                                  appearance: {
                                    ...currentAppearance,
                                    distinctiveFeatures: newFeatures.length === 0 ? false : newFeatures
                                  }
                                })
                              }
                            }}
                            className="justify-start text-left"
                          >
                            {isLocked ? <Lock className="h-3 w-3 mr-1 flex-shrink-0" /> : <Unlock className="h-3 w-3 mr-1 flex-shrink-0" />}
                            <span className="truncate">{feature}</span>
                          </Button>
                        )
                      })}
                    </div>
                  </div>
                )}
                
                {/* 音の個別ロック */}
                {existingResult?.appearance?.sounds && existingResult.appearance.sounds.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium">発する音</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      <Button
                        variant={
                          typeof lockedSections.appearance === 'object' && lockedSections.appearance?.sounds === true 
                            ? "default" 
                            : "outline"
                        }
                        size="sm"
                        onClick={() => {
                          if (onLockedSectionsChange) {
                            const currentAppearance = typeof lockedSections.appearance === 'object' ? lockedSections.appearance : {}
                            onLockedSectionsChange({
                              ...lockedSections,
                              appearance: {
                                ...currentAppearance,
                                sounds: currentAppearance.sounds === true ? false : true
                              }
                            })
                          }
                        }}
                        className="justify-start"
                      >
                        {typeof lockedSections.appearance === 'object' && lockedSections.appearance?.sounds === true 
                          ? <Lock className="h-3 w-3 mr-1" /> 
                          : <Unlock className="h-3 w-3 mr-1" />}
                        音全体
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ml-4">
                      {existingResult.appearance.sounds.map((sound, index) => {
                        const isLocked = typeof lockedSections.appearance === 'object' && (
                          Array.isArray(lockedSections.appearance.sounds) 
                            ? lockedSections.appearance.sounds.includes(index)
                            : lockedSections.appearance.sounds === true
                        )
                        return (
                          <Button
                            key={index}
                            variant={isLocked ? "default" : "outline"}
                            size="sm"
                            onClick={() => {
                              if (onLockedSectionsChange) {
                                const currentAppearance = typeof lockedSections.appearance === 'object' ? lockedSections.appearance : {}
                                let newSounds: boolean | number[]
                                if (Array.isArray(currentAppearance.sounds)) {
                                  const current = currentAppearance.sounds
                                  if (current.includes(index)) {
                                    newSounds = current.filter(i => i !== index)
                                  } else {
                                    newSounds = [...current, index]
                                  }
                                } else {
                                  newSounds = isLocked ? [] : [index]
                                }
                                onLockedSectionsChange({
                                  ...lockedSections,
                                  appearance: {
                                    ...currentAppearance,
                                    sounds: newSounds.length === 0 ? false : newSounds
                                  }
                                })
                              }
                            }}
                            className="justify-start text-left"
                          >
                            {isLocked ? <Lock className="h-3 w-3 mr-1 flex-shrink-0" /> : <Unlock className="h-3 w-3 mr-1 flex-shrink-0" />}
                            <span className="truncate">{sound}</span>
                          </Button>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
            
            {/* 能力タブ */}
            <TabsContent value="abilities" className="space-y-4">
              <div className="space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  <Button
                    variant={lockedSections.abilities === true ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      if (onLockedSectionsChange) {
                        onLockedSectionsChange({
                          ...lockedSections,
                          abilities: lockedSections.abilities === true ? false : true
                        })
                      }
                    }}
                    className="justify-start"
                  >
                    {lockedSections.abilities === true ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                    能力全体
                  </Button>
                </div>
                
                {/* 攻撃パターンの個別ロック */}
                {existingResult?.abilities?.combatCapabilities?.attackPatterns && existingResult.abilities.combatCapabilities.attackPatterns.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium">攻撃パターン</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      <Button
                        variant={
                          typeof lockedSections.abilities === 'object' && 
                          typeof lockedSections.abilities.combatCapabilities === 'object' &&
                          lockedSections.abilities.combatCapabilities?.attackPatterns === true 
                            ? "default" 
                            : "outline"
                        }
                        size="sm"
                        onClick={() => {
                          if (onLockedSectionsChange) {
                            const currentAbilities = typeof lockedSections.abilities === 'object' ? lockedSections.abilities : {}
                            const currentCombat = typeof currentAbilities.combatCapabilities === 'object' ? currentAbilities.combatCapabilities : {}
                            onLockedSectionsChange({
                              ...lockedSections,
                              abilities: {
                                ...currentAbilities,
                                combatCapabilities: {
                                  ...currentCombat,
                                  attackPatterns: currentCombat.attackPatterns === true ? false : true
                                }
                              }
                            })
                          }
                        }}
                        className="justify-start"
                      >
                        {typeof lockedSections.abilities === 'object' && 
                         typeof lockedSections.abilities.combatCapabilities === 'object' &&
                         lockedSections.abilities.combatCapabilities?.attackPatterns === true 
                          ? <Lock className="h-3 w-3 mr-1" /> 
                          : <Unlock className="h-3 w-3 mr-1" />}
                        攻撃全体
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ml-4">
                      {existingResult.abilities.combatCapabilities.attackPatterns.map((attack, index) => {
                        const isLocked = typeof lockedSections.abilities === 'object' && 
                          typeof lockedSections.abilities.combatCapabilities === 'object' && (
                          Array.isArray(lockedSections.abilities.combatCapabilities.attackPatterns) 
                            ? lockedSections.abilities.combatCapabilities.attackPatterns.includes(index)
                            : lockedSections.abilities.combatCapabilities.attackPatterns === true
                        )
                        return (
                          <Button
                            key={index}
                            variant={isLocked ? "default" : "outline"}
                            size="sm"
                            onClick={() => {
                              if (onLockedSectionsChange) {
                                const currentAbilities = typeof lockedSections.abilities === 'object' ? lockedSections.abilities : {}
                                const currentCombat = typeof currentAbilities.combatCapabilities === 'object' ? currentAbilities.combatCapabilities : {}
                                let newAttacks: boolean | number[]
                                if (Array.isArray(currentCombat.attackPatterns)) {
                                  const current = currentCombat.attackPatterns
                                  if (current.includes(index)) {
                                    newAttacks = current.filter(i => i !== index)
                                  } else {
                                    newAttacks = [...current, index]
                                  }
                                } else {
                                  newAttacks = isLocked ? [] : [index]
                                }
                                onLockedSectionsChange({
                                  ...lockedSections,
                                  abilities: {
                                    ...currentAbilities,
                                    combatCapabilities: {
                                      ...currentCombat,
                                      attackPatterns: newAttacks.length === 0 ? false : newAttacks
                                    }
                                  }
                                })
                              }
                            }}
                            className="justify-start text-left"
                          >
                            {isLocked ? <Lock className="h-3 w-3 mr-1 flex-shrink-0" /> : <Unlock className="h-3 w-3 mr-1 flex-shrink-0" />}
                            <span className="truncate">{attack.name}</span>
                          </Button>
                        )
                      })}
                    </div>
                  </div>
                )}
                
                {/* 特殊能力の個別ロック */}
                {existingResult?.abilities?.combatCapabilities?.specialAbilities && existingResult.abilities.combatCapabilities.specialAbilities.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium">特殊能力</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      <Button
                        variant={
                          typeof lockedSections.abilities === 'object' && 
                          typeof lockedSections.abilities.combatCapabilities === 'object' &&
                          lockedSections.abilities.combatCapabilities?.specialAbilities === true 
                            ? "default" 
                            : "outline"
                        }
                        size="sm"
                        onClick={() => {
                          if (onLockedSectionsChange) {
                            const currentAbilities = typeof lockedSections.abilities === 'object' ? lockedSections.abilities : {}
                            const currentCombat = typeof currentAbilities.combatCapabilities === 'object' ? currentAbilities.combatCapabilities : {}
                            onLockedSectionsChange({
                              ...lockedSections,
                              abilities: {
                                ...currentAbilities,
                                combatCapabilities: {
                                  ...currentCombat,
                                  specialAbilities: currentCombat.specialAbilities === true ? false : true
                                }
                              }
                            })
                          }
                        }}
                        className="justify-start"
                      >
                        {typeof lockedSections.abilities === 'object' && 
                         typeof lockedSections.abilities.combatCapabilities === 'object' &&
                         lockedSections.abilities.combatCapabilities?.specialAbilities === true 
                          ? <Lock className="h-3 w-3 mr-1" /> 
                          : <Unlock className="h-3 w-3 mr-1" />}
                        特殊能力全体
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ml-4">
                      {existingResult.abilities.combatCapabilities.specialAbilities.map((ability, index) => {
                        const isLocked = typeof lockedSections.abilities === 'object' && 
                          typeof lockedSections.abilities.combatCapabilities === 'object' && (
                          Array.isArray(lockedSections.abilities.combatCapabilities.specialAbilities) 
                            ? lockedSections.abilities.combatCapabilities.specialAbilities.includes(index)
                            : lockedSections.abilities.combatCapabilities.specialAbilities === true
                        )
                        return (
                          <Button
                            key={index}
                            variant={isLocked ? "default" : "outline"}
                            size="sm"
                            onClick={() => {
                              if (onLockedSectionsChange) {
                                const currentAbilities = typeof lockedSections.abilities === 'object' ? lockedSections.abilities : {}
                                const currentCombat = typeof currentAbilities.combatCapabilities === 'object' ? currentAbilities.combatCapabilities : {}
                                let newAbilities: boolean | number[]
                                if (Array.isArray(currentCombat.specialAbilities)) {
                                  const current = currentCombat.specialAbilities
                                  if (current.includes(index)) {
                                    newAbilities = current.filter(i => i !== index)
                                  } else {
                                    newAbilities = [...current, index]
                                  }
                                } else {
                                  newAbilities = isLocked ? [] : [index]
                                }
                                onLockedSectionsChange({
                                  ...lockedSections,
                                  abilities: {
                                    ...currentAbilities,
                                    combatCapabilities: {
                                      ...currentCombat,
                                      specialAbilities: newAbilities.length === 0 ? false : newAbilities
                                    }
                                  }
                                })
                              }
                            }}
                            className="justify-start text-left"
                          >
                            {isLocked ? <Lock className="h-3 w-3 mr-1 flex-shrink-0" /> : <Unlock className="h-3 w-3 mr-1 flex-shrink-0" />}
                            <span className="truncate">{ability.name}</span>
                          </Button>
                        )
                      })}
                    </div>
                  </div>
                )}
                
                {/* 弱点の個別ロック */}
                {existingResult?.abilities?.weaknesses && existingResult.abilities.weaknesses.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium">弱点</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      <Button
                        variant={
                          typeof lockedSections.abilities === 'object' && 
                          lockedSections.abilities?.weaknesses === true 
                            ? "default" 
                            : "outline"
                        }
                        size="sm"
                        onClick={() => {
                          if (onLockedSectionsChange) {
                            const currentAbilities = typeof lockedSections.abilities === 'object' ? lockedSections.abilities : {}
                            onLockedSectionsChange({
                              ...lockedSections,
                              abilities: {
                                ...currentAbilities,
                                weaknesses: currentAbilities.weaknesses === true ? false : true
                              }
                            })
                          }
                        }}
                        className="justify-start"
                      >
                        {typeof lockedSections.abilities === 'object' && 
                         lockedSections.abilities?.weaknesses === true 
                          ? <Lock className="h-3 w-3 mr-1" /> 
                          : <Unlock className="h-3 w-3 mr-1" />}
                        弱点全体
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ml-4">
                      {existingResult.abilities.weaknesses.map((weakness, index) => {
                        const isLocked = typeof lockedSections.abilities === 'object' && (
                          Array.isArray(lockedSections.abilities.weaknesses) 
                            ? lockedSections.abilities.weaknesses.includes(index)
                            : lockedSections.abilities.weaknesses === true
                        )
                        return (
                          <Button
                            key={index}
                            variant={isLocked ? "default" : "outline"}
                            size="sm"
                            onClick={() => {
                              if (onLockedSectionsChange) {
                                const currentAbilities = typeof lockedSections.abilities === 'object' ? lockedSections.abilities : {}
                                let newWeaknesses: boolean | number[]
                                if (Array.isArray(currentAbilities.weaknesses)) {
                                  const current = currentAbilities.weaknesses
                                  if (current.includes(index)) {
                                    newWeaknesses = current.filter(i => i !== index)
                                  } else {
                                    newWeaknesses = [...current, index]
                                  }
                                } else {
                                  newWeaknesses = isLocked ? [] : [index]
                                }
                                onLockedSectionsChange({
                                  ...lockedSections,
                                  abilities: {
                                    ...currentAbilities,
                                    weaknesses: newWeaknesses.length === 0 ? false : newWeaknesses
                                  }
                                })
                              }
                            }}
                            className="justify-start text-left"
                          >
                            {isLocked ? <Lock className="h-3 w-3 mr-1 flex-shrink-0" /> : <Unlock className="h-3 w-3 mr-1 flex-shrink-0" />}
                            <span className="truncate">{weakness.type}</span>
                          </Button>
                        )
                      })}
                    </div>
                  </div>
                )}
                
                {/* 耐性の個別ロック */}
                {existingResult?.abilities?.resistances && existingResult.abilities.resistances.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium">耐性</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      <Button
                        variant={
                          typeof lockedSections.abilities === 'object' && 
                          lockedSections.abilities?.resistances === true 
                            ? "default" 
                            : "outline"
                        }
                        size="sm"
                        onClick={() => {
                          if (onLockedSectionsChange) {
                            const currentAbilities = typeof lockedSections.abilities === 'object' ? lockedSections.abilities : {}
                            onLockedSectionsChange({
                              ...lockedSections,
                              abilities: {
                                ...currentAbilities,
                                resistances: currentAbilities.resistances === true ? false : true
                              }
                            })
                          }
                        }}
                        className="justify-start"
                      >
                        {typeof lockedSections.abilities === 'object' && 
                         lockedSections.abilities?.resistances === true 
                          ? <Lock className="h-3 w-3 mr-1" /> 
                          : <Unlock className="h-3 w-3 mr-1" />}
                        耐性全体
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ml-4">
                      {existingResult.abilities.resistances.map((resistance, index) => {
                        const isLocked = typeof lockedSections.abilities === 'object' && (
                          Array.isArray(lockedSections.abilities.resistances) 
                            ? lockedSections.abilities.resistances.includes(index)
                            : lockedSections.abilities.resistances === true
                        )
                        return (
                          <Button
                            key={index}
                            variant={isLocked ? "default" : "outline"}
                            size="sm"
                            onClick={() => {
                              if (onLockedSectionsChange) {
                                const currentAbilities = typeof lockedSections.abilities === 'object' ? lockedSections.abilities : {}
                                let newResistances: boolean | number[]
                                if (Array.isArray(currentAbilities.resistances)) {
                                  const current = currentAbilities.resistances
                                  if (current.includes(index)) {
                                    newResistances = current.filter(i => i !== index)
                                  } else {
                                    newResistances = [...current, index]
                                  }
                                } else {
                                  newResistances = isLocked ? [] : [index]
                                }
                                onLockedSectionsChange({
                                  ...lockedSections,
                                  abilities: {
                                    ...currentAbilities,
                                    resistances: newResistances.length === 0 ? false : newResistances
                                  }
                                })
                              }
                            }}
                            className="justify-start text-left"
                          >
                            {isLocked ? <Lock className="h-3 w-3 mr-1 flex-shrink-0" /> : <Unlock className="h-3 w-3 mr-1 flex-shrink-0" />}
                            <span className="truncate">{resistance.type}</span>
                          </Button>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
            
            {/* 行動タブ */}
            <TabsContent value="behavior" className="space-y-4">
              <div className="space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  <Button
                    variant={lockedSections.behavior === true ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      if (onLockedSectionsChange) {
                        onLockedSectionsChange({
                          ...lockedSections,
                          behavior: lockedSections.behavior === true ? false : true
                        })
                      }
                    }}
                    className="justify-start"
                  >
                    {lockedSections.behavior === true ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                    行動全体
                  </Button>
                </div>
                
                {/* 基本的な行動項目 */}
                <div className="space-y-2">
                  <div className="text-sm font-medium">基本行動パターン</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 ml-4">
                    {[
                      { key: 'aiPattern', label: 'AI行動パターン' },
                      { key: 'intelligenceDescription', label: '知能説明' },
                      { key: 'huntingPattern', label: '狩りのパターン' }
                    ].map(item => {
                      const isLocked = typeof lockedSections.behavior === 'object' 
                        ? lockedSections.behavior[item.key as keyof typeof lockedSections.behavior]
                        : lockedSections.behavior === true
                      return (
                        <Button
                          key={item.key}
                          variant={isLocked ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            if (onLockedSectionsChange) {
                              const currentBehavior = typeof lockedSections.behavior === 'object' 
                                ? lockedSections.behavior 
                                : {}
                              onLockedSectionsChange({
                                ...lockedSections,
                                behavior: {
                                  ...currentBehavior,
                                  [item.key]: !isLocked
                                }
                              })
                            }
                          }}
                          className="justify-start"
                        >
                          {isLocked ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                          {item.label}
                        </Button>
                      )
                    })}
                  </div>
                </div>
                
                {/* トリガーの個別ロック */}
                {existingResult?.behavior?.triggers && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium">行動トリガー</div>
                    <div className="ml-4 space-y-2">
                      {/* 攻撃的トリガー */}
                      {existingResult.behavior.triggers.aggressive && existingResult.behavior.triggers.aggressive.length > 0 && (
                        <div className="space-y-1">
                          <div className="text-xs text-muted-foreground">攻撃的になる条件</div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            <Button
                              variant={
                                typeof lockedSections.behavior === 'object' && 
                                typeof lockedSections.behavior.triggers === 'object' &&
                                lockedSections.behavior.triggers?.aggressive === true 
                                  ? "default" 
                                  : "outline"
                              }
                              size="sm"
                              onClick={() => {
                                if (onLockedSectionsChange) {
                                  const currentBehavior = typeof lockedSections.behavior === 'object' ? lockedSections.behavior : {}
                                  const currentTriggers = typeof currentBehavior.triggers === 'object' ? currentBehavior.triggers : {}
                                  onLockedSectionsChange({
                                    ...lockedSections,
                                    behavior: {
                                      ...currentBehavior,
                                      triggers: {
                                        ...currentTriggers,
                                        aggressive: currentTriggers.aggressive === true ? false : true
                                      }
                                    }
                                  })
                                }
                              }}
                              className="justify-start text-xs"
                            >
                              {typeof lockedSections.behavior === 'object' && 
                               typeof lockedSections.behavior.triggers === 'object' &&
                               lockedSections.behavior.triggers?.aggressive === true 
                                ? <Lock className="h-3 w-3 mr-1" /> 
                                : <Unlock className="h-3 w-3 mr-1" />}
                              攻撃的全体
                            </Button>
                          </div>
                        </div>
                      )}
                      
                      {/* 防御的トリガー */}
                      {existingResult.behavior.triggers.defensive && existingResult.behavior.triggers.defensive.length > 0 && (
                        <div className="space-y-1">
                          <div className="text-xs text-muted-foreground">防御的になる条件</div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            <Button
                              variant={
                                typeof lockedSections.behavior === 'object' && 
                                typeof lockedSections.behavior.triggers === 'object' &&
                                lockedSections.behavior.triggers?.defensive === true 
                                  ? "default" 
                                  : "outline"
                              }
                              size="sm"
                              onClick={() => {
                                if (onLockedSectionsChange) {
                                  const currentBehavior = typeof lockedSections.behavior === 'object' ? lockedSections.behavior : {}
                                  const currentTriggers = typeof currentBehavior.triggers === 'object' ? currentBehavior.triggers : {}
                                  onLockedSectionsChange({
                                    ...lockedSections,
                                    behavior: {
                                      ...currentBehavior,
                                      triggers: {
                                        ...currentTriggers,
                                        defensive: currentTriggers.defensive === true ? false : true
                                      }
                                    }
                                  })
                                }
                              }}
                              className="justify-start text-xs"
                            >
                              {typeof lockedSections.behavior === 'object' && 
                               typeof lockedSections.behavior.triggers === 'object' &&
                               lockedSections.behavior.triggers?.defensive === true 
                                ? <Lock className="h-3 w-3 mr-1" /> 
                                : <Unlock className="h-3 w-3 mr-1" />}
                              防御的全体
                            </Button>
                          </div>
                        </div>
                      )}
                      
                      {/* 逃走トリガー */}
                      {existingResult.behavior.triggers.flee && existingResult.behavior.triggers.flee.length > 0 && (
                        <div className="space-y-1">
                          <div className="text-xs text-muted-foreground">逃走する条件</div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            <Button
                              variant={
                                typeof lockedSections.behavior === 'object' && 
                                typeof lockedSections.behavior.triggers === 'object' &&
                                lockedSections.behavior.triggers?.flee === true 
                                  ? "default" 
                                  : "outline"
                              }
                              size="sm"
                              onClick={() => {
                                if (onLockedSectionsChange) {
                                  const currentBehavior = typeof lockedSections.behavior === 'object' ? lockedSections.behavior : {}
                                  const currentTriggers = typeof currentBehavior.triggers === 'object' ? currentBehavior.triggers : {}
                                  onLockedSectionsChange({
                                    ...lockedSections,
                                    behavior: {
                                      ...currentBehavior,
                                      triggers: {
                                        ...currentTriggers,
                                        flee: currentTriggers.flee === true ? false : true
                                      }
                                    }
                                  })
                                }
                              }}
                              className="justify-start text-xs"
                            >
                              {typeof lockedSections.behavior === 'object' && 
                               typeof lockedSections.behavior.triggers === 'object' &&
                               lockedSections.behavior.triggers?.flee === true 
                                ? <Lock className="h-3 w-3 mr-1" /> 
                                : <Unlock className="h-3 w-3 mr-1" />}
                              逃走全体
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* フェーズの個別ロック */}
                {existingResult?.behavior?.phases && existingResult.behavior.phases.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium">戦闘フェーズ</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      <Button
                        variant={
                          typeof lockedSections.behavior === 'object' && 
                          lockedSections.behavior?.phases === true 
                            ? "default" 
                            : "outline"
                        }
                        size="sm"
                        onClick={() => {
                          if (onLockedSectionsChange) {
                            const currentBehavior = typeof lockedSections.behavior === 'object' ? lockedSections.behavior : {}
                            onLockedSectionsChange({
                              ...lockedSections,
                              behavior: {
                                ...currentBehavior,
                                phases: currentBehavior.phases === true ? false : true
                              }
                            })
                          }
                        }}
                        className="justify-start"
                      >
                        {typeof lockedSections.behavior === 'object' && 
                         lockedSections.behavior?.phases === true 
                          ? <Lock className="h-3 w-3 mr-1" /> 
                          : <Unlock className="h-3 w-3 mr-1" />}
                        フェーズ全体
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ml-4">
                      {existingResult.behavior.phases.map((phase, index) => {
                        const isLocked = typeof lockedSections.behavior === 'object' && (
                          Array.isArray(lockedSections.behavior.phases) 
                            ? lockedSections.behavior.phases.includes(index)
                            : lockedSections.behavior.phases === true
                        )
                        return (
                          <Button
                            key={index}
                            variant={isLocked ? "default" : "outline"}
                            size="sm"
                            onClick={() => {
                              if (onLockedSectionsChange) {
                                const currentBehavior = typeof lockedSections.behavior === 'object' ? lockedSections.behavior : {}
                                let newPhases: boolean | number[]
                                if (Array.isArray(currentBehavior.phases)) {
                                  const current = currentBehavior.phases
                                  if (current.includes(index)) {
                                    newPhases = current.filter(i => i !== index)
                                  } else {
                                    newPhases = [...current, index]
                                  }
                                } else {
                                  newPhases = isLocked ? [] : [index]
                                }
                                onLockedSectionsChange({
                                  ...lockedSections,
                                  behavior: {
                                    ...currentBehavior,
                                    phases: newPhases.length === 0 ? false : newPhases
                                  }
                                })
                              }
                            }}
                            className="justify-start text-left"
                          >
                            {isLocked ? <Lock className="h-3 w-3 mr-1 flex-shrink-0" /> : <Unlock className="h-3 w-3 mr-1 flex-shrink-0" />}
                            <span className="truncate">フェーズ{phase.phase}: {phase.name}</span>
                          </Button>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
            
            {/* 伝承タブ */}
            <TabsContent value="lore" className="space-y-4">
              <div className="space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  <Button
                    variant={lockedSections.lore === true ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      if (onLockedSectionsChange) {
                        onLockedSectionsChange({
                          ...lockedSections,
                          lore: lockedSections.lore === true ? false : true
                        })
                      }
                    }}
                    className="justify-start"
                  >
                    {lockedSections.lore === true ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                    伝承全体
                  </Button>
                </div>
                
                {/* 起源の物語 */}
                {existingResult?.lore?.originStory && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium">起源の物語</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 ml-4">
                      {[
                        { key: 'era', label: '時代' },
                        { key: 'location', label: '場所' },
                        { key: 'narrative', label: '物語' }
                      ].map(item => {
                        const isLocked = typeof lockedSections.lore === 'object' && 
                          typeof lockedSections.lore.originStory === 'object' &&
                          lockedSections.lore.originStory[item.key as keyof typeof lockedSections.lore.originStory]
                        return (
                          <Button
                            key={item.key}
                            variant={isLocked ? "default" : "outline"}
                            size="sm"
                            onClick={() => {
                              if (onLockedSectionsChange) {
                                const currentLore = typeof lockedSections.lore === 'object' ? lockedSections.lore : {}
                                const currentOrigin = typeof currentLore.originStory === 'object' ? currentLore.originStory : {}
                                onLockedSectionsChange({
                                  ...lockedSections,
                                  lore: {
                                    ...currentLore,
                                    originStory: {
                                      ...currentOrigin,
                                      [item.key]: !isLocked
                                    }
                                  }
                                })
                              }
                            }}
                            className="justify-start"
                          >
                            {isLocked ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                            {item.label}
                          </Button>
                        )
                      })}
                    </div>
                  </div>
                )}
                
              </div>
            </TabsContent>
            
            {/* 遭遇タブ */}
            <TabsContent value="encounter" className="space-y-4">
              <div className="space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  <Button
                    variant={lockedSections.encounter === true ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      if (onLockedSectionsChange) {
                        onLockedSectionsChange({
                          ...lockedSections,
                          encounter: lockedSections.encounter === true ? false : true
                        })
                      }
                    }}
                    className="justify-start"
                  >
                    {lockedSections.encounter === true ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                    遭遇全体
                  </Button>
                </div>
                
                {/* 遭遇条件 */}
                {existingResult?.encounter?.conditions && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium">遭遇条件</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 ml-4">
                      {[
                        { key: 'location', label: '場所', hasArray: true },
                        { key: 'time', label: '時間', hasArray: true }
                      ].map(item => {
                        const isLocked = typeof lockedSections.encounter === 'object' && 
                          typeof lockedSections.encounter.conditions === 'object' &&
                          lockedSections.encounter.conditions[item.key as keyof typeof lockedSections.encounter.conditions]
                        return (
                          <Button
                            key={item.key}
                            variant={isLocked ? "default" : "outline"}
                            size="sm"
                            onClick={() => {
                              if (onLockedSectionsChange) {
                                const currentEncounter = typeof lockedSections.encounter === 'object' ? lockedSections.encounter : {}
                                const currentConditions = typeof currentEncounter.conditions === 'object' ? currentEncounter.conditions : {}
                                onLockedSectionsChange({
                                  ...lockedSections,
                                  encounter: {
                                    ...currentEncounter,
                                    conditions: {
                                      ...currentConditions,
                                      [item.key]: !isLocked
                                    }
                                  }
                                })
                              }
                            }}
                            className="justify-start"
                          >
                            {isLocked ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                            {item.label}
                          </Button>
                        )
                      })}
                    </div>
                  </div>
                )}
                
              </div>
            </TabsContent>
          </Tabs>
        </div>
      )}
      
      <Tabs value={input.mode || 'standard'} onValueChange={handleModeChange}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="standard">標準モード</TabsTrigger>
          <TabsTrigger value="idea">アイディアモード</TabsTrigger>
          <TabsTrigger value="batch">バッチモード</TabsTrigger>
        </TabsList>

        <TabsContent value="standard" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">名前（省略可）</Label>
              <Input
                id="name"
                value={input.name || ''}
                onChange={(e) => onChange({ ...input, name: e.target.value })}
                placeholder="自動生成"
              />
            </div>
            
            <div>
              <Label htmlFor="type">タイプ</Label>
              <Select value={input.type || ''} onValueChange={(v) => onChange({ ...input, type: v as EnemyInput['type'] })}>
                <SelectTrigger>
                  <SelectValue placeholder="選択してください" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="minion">ザコ敵</SelectItem>
                  <SelectItem value="elite">エリート</SelectItem>
                  <SelectItem value="mini_boss">中ボス</SelectItem>
                  <SelectItem value="boss">ボス</SelectItem>
                  <SelectItem value="secret_boss">隠しボス</SelectItem>
                  <SelectItem value="raid_boss">レイドボス</SelectItem>
                  <SelectItem value="unique">ユニーク</SelectItem>
                  <SelectItem value="custom">カスタム</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {input.type === 'custom' && (
              <div className="col-span-2">
                <Label htmlFor="customType">カスタムタイプ</Label>
                <Input
                  id="customType"
                  value={input.customType || ''}
                  onChange={(e) => onChange({ ...input, customType: e.target.value })}
                  placeholder="例：古代の守護者"
                />
              </div>
            )}

            <div>
              <Label htmlFor="category">カテゴリ</Label>
              <Select value={input.category || ''} onValueChange={(v) => onChange({ ...input, category: v as EnemyInput['category'] })}>
                <SelectTrigger>
                  <SelectValue placeholder="選択してください" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="beast">獣</SelectItem>
                  <SelectItem value="undead">アンデッド</SelectItem>
                  <SelectItem value="demon">悪魔</SelectItem>
                  <SelectItem value="elemental">精霊</SelectItem>
                  <SelectItem value="mechanical">機械</SelectItem>
                  <SelectItem value="humanoid">人型</SelectItem>
                  <SelectItem value="dragon">ドラゴン</SelectItem>
                  <SelectItem value="aberration">異形</SelectItem>
                  <SelectItem value="plant">植物</SelectItem>
                  <SelectItem value="construct">構造物</SelectItem>
                  <SelectItem value="spirit">霊体</SelectItem>
                  <SelectItem value="yokai">妖怪</SelectItem>
                  <SelectItem value="urban_legend">都市伝説</SelectItem>
                  <SelectItem value="cryptid">UMA</SelectItem>
                  <SelectItem value="custom">カスタム</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {input.category === 'custom' && (
              <div>
                <Label htmlFor="customCategory">カスタムカテゴリ</Label>
                <Input
                  id="customCategory"
                  value={input.customCategory || ''}
                  onChange={(e) => onChange({ ...input, customCategory: e.target.value })}
                  placeholder="例：電脳生命体"
                />
              </div>
            )}

            <div>
              <Label htmlFor="genre">ジャンル</Label>
              <Select value={input.genre || ''} onValueChange={(v) => onChange({ ...input, genre: v as EnemyInput['genre'] })}>
                <SelectTrigger>
                  <SelectValue placeholder="選択してください" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fantasy">ファンタジー</SelectItem>
                  <SelectItem value="sf">SF</SelectItem>
                  <SelectItem value="horror">ホラー</SelectItem>
                  <SelectItem value="post_apocalypse">ポストアポカリプス</SelectItem>
                  <SelectItem value="cyberpunk">サイバーパンク</SelectItem>
                  <SelectItem value="mythology">神話</SelectItem>
                  <SelectItem value="modern">現代</SelectItem>
                  <SelectItem value="urban_legend_jp">日本の都市伝説</SelectItem>
                  <SelectItem value="urban_legend_west">西洋の都市伝説</SelectItem>
                  <SelectItem value="yokai">妖怪</SelectItem>
                  <SelectItem value="scp">SCP</SelectItem>
                  <SelectItem value="cosmic_horror">コズミックホラー</SelectItem>
                  <SelectItem value="fairy_tale">童話</SelectItem>
                  <SelectItem value="custom">カスタム</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {input.genre === 'custom' && (
              <div>
                <Label htmlFor="customGenre">カスタムジャンル</Label>
                <Input
                  id="customGenre"
                  value={input.customGenre || ''}
                  onChange={(e) => onChange({ ...input, customGenre: e.target.value })}
                  placeholder="例：和風スチームパンク"
                />
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="threatLevel">脅威度</Label>
              <Select
                value={input.threatLevel || 'C'}
                onValueChange={(v) => onChange({ ...input, threatLevel: v as any })}
              >
                <SelectTrigger id="threatLevel">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="E">E - 一般人でもそれほど脅威ではない</SelectItem>
                  <SelectItem value="D">D - 武装した一般人なら対処可能</SelectItem>
                  <SelectItem value="C">C - 訓練された戦士が必要</SelectItem>
                  <SelectItem value="B">B - 熟練の冒険者パーティが必要</SelectItem>
                  <SelectItem value="A">A - 国家レベルの戦力が必要</SelectItem>
                  <SelectItem value="S">S - 伝説級の英雄が必要</SelectItem>
                  <SelectItem value="SS">SS - 世界の危機</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="difficulty">難易度: {input.difficultyLevel || 5}</Label>
              <Slider
                id="difficulty"
                value={[input.difficultyLevel || 5]}
                onValueChange={(v) => onChange({ ...input, difficultyLevel: v[0] })}
                min={1}
                max={10}
                step={1}
              />
            </div>

            <div>
              <Label htmlFor="intelligence">知能レベル</Label>
              <Select value={input.intelligenceLevel || 'medium'} onValueChange={(v) => onChange({ ...input, intelligenceLevel: v as EnemyInput['intelligenceLevel'] })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">なし</SelectItem>
                  <SelectItem value="low">低い</SelectItem>
                  <SelectItem value="medium">普通</SelectItem>
                  <SelectItem value="high">高い</SelectItem>
                  <SelectItem value="genius">天才</SelectItem>
                  <SelectItem value="incomprehensible">理解不能</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="habitat">生息地・出現場所</Label>
              <Input
                id="habitat"
                value={input.habitat || ''}
                onChange={(e) => onChange({ ...input, habitat: e.target.value })}
                placeholder="例：廃墟、深夜の学校、鏡の中"
              />
            </div>

            <div>
              <Label htmlFor="timeOfActivity">活動時間帯</Label>
              <Select value={input.timeOfActivity || 'anytime'} onValueChange={(v) => onChange({ ...input, timeOfActivity: v as EnemyInput['timeOfActivity'] })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">昼間</SelectItem>
                  <SelectItem value="night">夜間</SelectItem>
                  <SelectItem value="twilight">黄昏時</SelectItem>
                  <SelectItem value="anytime">いつでも</SelectItem>
                  <SelectItem value="specific">特定時刻</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {input.timeOfActivity === 'specific' && (
              <div>
                <Label htmlFor="specificTime">特定時刻</Label>
                <Input
                  id="specificTime"
                  value={input.specificTime || ''}
                  onChange={(e) => onChange({ ...input, specificTime: e.target.value })}
                  placeholder="例：丑三つ時、午前3時33分"
                />
              </div>
            )}

            {(input.genre === 'urban_legend_jp' || input.genre === 'urban_legend_west' || input.genre === 'yokai' || input.category === 'urban_legend' || input.category === 'yokai') && (
              <>
                <div>
                  <Label htmlFor="legendOrigin">伝承の起源</Label>
                  <Select value={input.legendOrigin || ''} onValueChange={(v) => onChange({ ...input, legendOrigin: v as EnemyInput['legendOrigin'] })}>
                    <SelectTrigger>
                      <SelectValue placeholder="選択してください" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ancient">古代</SelectItem>
                      <SelectItem value="medieval">中世</SelectItem>
                      <SelectItem value="modern">近代</SelectItem>
                      <SelectItem value="internet">インターネット</SelectItem>
                      <SelectItem value="regional">地域伝承</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="regionOfOrigin">発祥地域</Label>
                  <Input
                    id="regionOfOrigin"
                    value={input.regionOfOrigin || ''}
                    onChange={(e) => onChange({ ...input, regionOfOrigin: e.target.value })}
                    placeholder="例：東京都、京都府、東北地方"
                  />
                </div>

                <div>
                  <Label htmlFor="summoning">召喚・遭遇条件</Label>
                  <Textarea
                    id="summoning"
                    value={input.summoning || ''}
                    onChange={(e) => onChange({ ...input, summoning: e.target.value })}
                    placeholder="例：鏡に向かって3回名前を呼ぶ、特定の場所で特定の言葉を唱える"
                    rows={2}
                  />
                </div>

                <div>
                  <Label htmlFor="taboo">禁忌・してはいけないこと</Label>
                  <Textarea
                    id="taboo"
                    value={input.taboo || ''}
                    onChange={(e) => onChange({ ...input, taboo: e.target.value })}
                    placeholder="例：目を合わせてはいけない、振り返ってはいけない"
                    rows={2}
                  />
                </div>
              </>
            )}

            <div>
              <Label htmlFor="additionalSettings">追加設定</Label>
              <Textarea
                id="additionalSettings"
                value={input.additionalSettings || ''}
                onChange={(e) => onChange({ ...input, additionalSettings: e.target.value })}
                placeholder="その他の詳細な設定や要望を入力"
                rows={3}
              />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="idea" className="space-y-4">
          <div className="mb-4 p-3 bg-muted rounded-md">
            <p className="text-sm text-muted-foreground">
              <strong>💡 統合生成モード：</strong>以下のすべての入力を組み合わせて使用できます
            </p>
            <ul className="text-xs text-muted-foreground mt-2 space-y-1 ml-4">
              <li>• アイディア入力 → 基本コンセプト</li>
              <li>• ファイル → 詳細な設定や背景</li>
              <li>• プロット → 物語上の役割</li>
              <li>• キャラクター → 因縁の相手として</li>
              <li>• エネミー → 関連する敵（上位種・同族・天敵など）</li>
            </ul>
            {(input.ideaText || input.uploadedContent || input.plotContext || input.characterContext || input.relatedEnemies?.length) && (
              <p className="text-xs font-medium mt-2">
                ✅ 現在の入力要素: {[
                  input.ideaText && 'アイディア',
                  input.uploadedContent && 'ファイル',
                  input.plotContext && 'プロット',
                  input.characterContext && 'キャラクター',
                  input.relatedEnemies?.length && 'エネミー'
                ].filter(Boolean).join('、')}
              </p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label htmlFor="ideaText">
                アイディア入力
                <span className="text-xs text-muted-foreground ml-2">（他の入力と組み合わせ可能）</span>
              </Label>
              {input.ideaText && (
                <span className="text-xs text-muted-foreground">
                  {input.ideaText.length}文字
                </span>
              )}
            </div>
            <Textarea
              id="ideaText"
              value={input.ideaText || ''}
              onChange={(e) => onChange({ ...input, ideaText: e.target.value })}
              placeholder="エネミーのアイディアを自由に入力してください（例：影を操る謎の存在、古代の封印から解き放たれた守護者）"
              rows={5}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label htmlFor="file">
                ファイルアップロード
                <span className="text-xs text-muted-foreground ml-2">（追加の参考資料）</span>
              </Label>
            </div>
            <input
              id="file-upload"
              type="file"
              accept=".txt"
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => document.getElementById('file-upload')?.click()}
              className="mb-2"
            >
              <Upload className="h-4 w-4 mr-2" />
              ファイルを選択
            </Button>
            {fileContent && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  ✅ ファイルを読み込みました（{fileContent.length}文字）
                </p>
                <Textarea
                  value={fileContent}
                  onChange={(e) => {
                    const newContent = e.target.value
                    setFileContent(newContent)
                    onChange({ ...input, uploadedContent: newContent })
                  }}
                  rows={3}
                  className="text-xs"
                  placeholder="ファイル内容のプレビュー（編集可能）"
                />
              </div>
            )}
          </div>

          {/* プロット選択 */}
          {savedPlots.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="plotSelect">
                  <FileText className="inline h-4 w-4 mr-1" />
                  保存済みプロットから選択
                  <span className="text-xs text-muted-foreground ml-2">（物語の敵として）</span>
                </Label>
              </div>
              <Select value={selectedPlotId} onValueChange={handlePlotSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="プロットを選択..." />
                </SelectTrigger>
                <SelectContent>
                  {savedPlots.map(plot => (
                    <SelectItem key={plot.id} value={plot.id}>
                      {plot.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedPlotId && (
                <div className="mt-2 p-3 bg-muted rounded-md">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      選択中: {savedPlots.find(p => p.id === selectedPlotId)?.title}
                    </p>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setSelectedPlotId('')
                        onChange({ ...input, plotContext: undefined })
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* キャラクター選択 */}
          {characters.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>
                  <Users className="inline h-4 w-4 mr-1" />
                  保存済みキャラクターから選択
                  <span className="text-xs text-muted-foreground ml-2">（宿敵・因縁の相手として）</span>
                </Label>
              </div>
              <div className="max-h-48 overflow-y-auto border rounded-md p-3 space-y-2">
                {characters.map(character => (
                  <div
                    key={character.id}
                    className="flex items-center space-x-2"
                  >
                    <Checkbox
                      id={`char-${character.id}`}
                      checked={selectedCharacterIds.includes(character.id)}
                      onCheckedChange={() => handleCharacterToggle(character.id)}
                    />
                    <Label
                      htmlFor={`char-${character.id}`}
                      className="text-sm cursor-pointer flex-1"
                    >
                      {character.result.name} - {character.result.role}
                      {character.result.age && ` (${character.result.age})`}
                    </Label>
                  </div>
                ))}
              </div>
              {selectedCharacterIds.length > 0 && (
                <p className="text-xs text-muted-foreground mt-2">
                  {selectedCharacterIds.length}人のキャラクターを選択中
                </p>
              )}
            </div>
          )}

          {/* 関連エネミー選択 */}
          {enemies.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>
                  <Sword className="inline h-4 w-4 mr-1" />
                  保存済みエネミーから選択
                  <span className="text-xs text-muted-foreground ml-2">（上位種・同族・天敵などの関連エネミーとして）</span>
                </Label>
              </div>
              <div className="max-h-48 overflow-y-auto border rounded-md p-3 space-y-2">
                {enemies.map(enemy => (
                  <div
                    key={enemy.id}
                    className="flex items-center space-x-2"
                  >
                    <Checkbox
                      id={`enemy-${enemy.id}`}
                      checked={selectedEnemyIds.includes(enemy.id)}
                      onCheckedChange={() => handleEnemyToggle(enemy.id)}
                    />
                    <Label
                      htmlFor={`enemy-${enemy.id}`}
                      className="text-sm cursor-pointer flex-1"
                    >
                      {enemy.result.basicInfo.name} - {enemy.result.basicInfo.type}
                      {enemy.result.basicInfo.threat && ` (脅威度: ${enemy.result.basicInfo.threat})`}
                    </Label>
                  </div>
                ))}
              </div>
              {selectedEnemyIds.length > 0 && (
                <p className="text-xs text-muted-foreground mt-2">
                  {selectedEnemyIds.length}体のエネミーを選択中
                </p>
              )}
            </div>
          )}

          {/* データがない場合のメッセージ */}
          {savedPlots.length === 0 && characters.length === 0 && enemies.length === 0 && (
            <div className="pt-2 border-t">
              <p className="text-xs text-muted-foreground">
                💡 プロット、キャラクター、エネミーを先に生成すると、それらと連携した敵を作成できます
              </p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="batch" className="space-y-4">
          {/* テーマ入力 */}
          <div>
            <Label htmlFor="batchTheme">バッチテーマ</Label>
            <Textarea
              id="batchTheme"
              value={input.batchTheme || ''}
              onChange={(e) => onChange({ ...input, batchTheme: e.target.value })}
              placeholder="生成する敵グループのテーマや共通コンセプトを入力してください
例：
・廃病院に潜む怪異たち
・古代遺跡の守護者たち
・機械化された森の支配者
・呪われた一族の末裔たち"
              rows={3}
            />
          </div>

          {/* 生成数 */}
          <div>
            <Label htmlFor="numberOfEnemies">生成数: {input.numberOfEnemies || 3}</Label>
            <Slider
              id="numberOfEnemies"
              value={[input.numberOfEnemies || 3]}
              onValueChange={(v) => onChange({ ...input, numberOfEnemies: v[0] })}
              min={2}
              max={25}
              step={1}
            />
            <p className="text-xs text-muted-foreground mt-2">
              ※ 5体ごとに1回のAPIを使用します（例: 18体 = 4回のAPI使用）
            </p>
          </div>

          {/* 生成パターン */}
          <div>
            <Label>生成パターン</Label>
            <Tabs value={input.batchPattern || 'hierarchical'} onValueChange={(v) => onChange({ ...input, batchPattern: v as 'hierarchical' | 'parallel' | 'progressive' })}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="hierarchical">階層型</TabsTrigger>
                <TabsTrigger value="parallel">並列型</TabsTrigger>
                <TabsTrigger value="progressive">段階型</TabsTrigger>
              </TabsList>
              <TabsContent value="hierarchical" className="mt-2">
                <p className="text-sm text-muted-foreground">
                  ボスと配下の構造（例：魔王1体 + 幹部2体 + 雑魚3体）
                </p>
              </TabsContent>
              <TabsContent value="parallel" className="mt-2">
                <p className="text-sm text-muted-foreground">
                  同格の存在たち（例：七つの大罪、四天王、五行の精霊）
                </p>
              </TabsContent>
              <TabsContent value="progressive" className="mt-2">
                <p className="text-sm text-muted-foreground">
                  成長・進化の段階（例：幼体→成体→完全体、弱い呪い→強い呪い）
                </p>
              </TabsContent>
            </Tabs>
          </div>

          {/* 脅威度分布 */}
          <div>
            <Label>脅威度分布</Label>
            <Tabs value={input.threatDistribution || 'auto'} onValueChange={(v) => onChange({ ...input, threatDistribution: v as 'auto' | 'same' | 'range' })}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="auto">自動調整</TabsTrigger>
                <TabsTrigger value="same">全て同じ</TabsTrigger>
                <TabsTrigger value="range">範囲指定</TabsTrigger>
              </TabsList>
              <TabsContent value="auto" className="mt-2">
                <p className="text-sm text-muted-foreground">
                  生成パターンに応じて適切に脅威度を振り分けます
                </p>
              </TabsContent>
              <TabsContent value="same" className="mt-2 space-y-2">
                <Select value={input.sameThreatLevel || 'C'} onValueChange={(value) => onChange({ ...input, sameThreatLevel: value as 'E' | 'D' | 'C' | 'B' | 'A' | 'S' | 'SS' })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="E">E - 一般人でも対処可能</SelectItem>
                    <SelectItem value="D">D - 訓練された者なら対処可能</SelectItem>
                    <SelectItem value="C">C - 専門家が必要</SelectItem>
                    <SelectItem value="B">B - チームでの対処が必要</SelectItem>
                    <SelectItem value="A">A - 組織的対応が必要</SelectItem>
                    <SelectItem value="S">S - 国家レベルの脅威</SelectItem>
                    <SelectItem value="SS">SS - 世界の危機</SelectItem>
                  </SelectContent>
                </Select>
              </TabsContent>
              <TabsContent value="range" className="mt-2 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>最小脅威度</Label>
                    <Select value={input.threatRange?.min || 'E'} onValueChange={(value) => onChange({ ...input, threatRange: { ...input.threatRange, min: value as 'E' | 'D' | 'C' | 'B' | 'A' | 'S' | 'SS', max: input.threatRange?.max || 'SS' } })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="E">E</SelectItem>
                        <SelectItem value="D">D</SelectItem>
                        <SelectItem value="C">C</SelectItem>
                        <SelectItem value="B">B</SelectItem>
                        <SelectItem value="A">A</SelectItem>
                        <SelectItem value="S">S</SelectItem>
                        <SelectItem value="SS">SS</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>最大脅威度</Label>
                    <Select value={input.threatRange?.max || 'SS'} onValueChange={(value) => onChange({ ...input, threatRange: { min: input.threatRange?.min || 'E', max: value as 'E' | 'D' | 'C' | 'B' | 'A' | 'S' | 'SS' } })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="E">E</SelectItem>
                        <SelectItem value="D">D</SelectItem>
                        <SelectItem value="C">C</SelectItem>
                        <SelectItem value="B">B</SelectItem>
                        <SelectItem value="A">A</SelectItem>
                        <SelectItem value="S">S</SelectItem>
                        <SelectItem value="SS">SS</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* 追加設定 */}
          <div>
            <Label htmlFor="batchAdditionalSettings">追加設定（任意）</Label>
            <Textarea
              id="batchAdditionalSettings"
              value={input.batchAdditionalSettings || ''}
              onChange={(e) => onChange({ ...input, batchAdditionalSettings: e.target.value })}
              placeholder="バッチ生成に関する追加の要望や設定があれば記入してください
例：
・共通の弱点：銀の武器
・共通の起源：1000年前の大戦
・必ず含める要素：全員が仮面を着けている
・除外する要素：人間型は避ける"
              rows={3}
            />
          </div>
        </TabsContent>
      </Tabs>

      {/* 共通設定 - すべてのモードで使用 */}
      <div className="space-y-4 border-t pt-4">
        <h3 className="text-sm font-medium text-muted-foreground">共通設定</h3>
        
        {/* 世界観選択 */}
        {savedWorlds.length > 0 && (
          <div>
            <Label htmlFor="world">世界観を選択（オプション）</Label>
            <Select 
              value={selectedWorldId || 'none'} 
              onValueChange={(value) => {
                if (value === 'none') {
                  setSelectedWorldId('')
                  onChange({ ...input, worldContext: undefined })
                } else {
                  setSelectedWorldId(value)
                  const world = savedWorlds.find(w => w.id === value)
                  if (world) {
                    // 統一された世界観コンテキスト生成関数を使用
                    const worldContext = generateWorldContextText(world.result)
                    onChange({ ...input, worldContext })
                  }
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="世界観を選択...">
                  {selectedWorldId ? (
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4" />
                      {savedWorlds.find(w => w.id === selectedWorldId)?.title}
                    </div>
                  ) : (
                    '世界観を選択...'
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">選択しない</SelectItem>
                {savedWorlds.map((world) => (
                  <SelectItem key={world.id} value={world.id}>
                    <div className="flex flex-col">
                      <div className="font-medium">{world.title}</div>
                      <div className="text-xs text-muted-foreground">
                        {world.result.summary?.substring(0, 50)}...
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedWorldId && (
              <div className="mt-2 p-2 bg-blue-50 rounded text-xs">
                <div className="font-medium text-blue-900 mb-1">選択中の世界観</div>
                <div className="text-blue-700">
                  {savedWorlds.find(w => w.id === selectedWorldId)?.result.summary?.substring(0, 100)}...
                </div>
                <div className="text-blue-600 mt-1">
                  ※ この世界観の脅威や文化に適合したエネミーが生成されます
                </div>
              </div>
            )}
          </div>
        )}
        
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="useProMode"
              checked={input.useProMode || false}
              onCheckedChange={(checked) => onChange({ ...input, useProMode: checked as boolean })}
            />
            <Label htmlFor="useProMode" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Proモードを使用（より詳細で高品質な生成）
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="useGemini3FlashMode"
              checked={input.useGemini3FlashMode || false}
              onCheckedChange={(checked) => onChange({ ...input, useGemini3FlashMode: checked as boolean })}
            />
            <Label htmlFor="useGemini3FlashMode" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Gemini 3 Flash モード（試験版）
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="useGemini3Mode"
              checked={input.useGemini3Mode || false}
              onCheckedChange={(checked) => onChange({ ...input, useGemini3Mode: checked as boolean })}
            />
            <Label htmlFor="useGemini3Mode" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Gemini 3 Pro モード（有料APIのみ・試験版）
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="useStreaming"
              checked={useStreaming}
              onCheckedChange={(checked) => onStreamingChange?.(!!checked)}
            />
            <Label htmlFor="useStreaming" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              ストリーミングモード（リアルタイム生成）
            </Label>
          </div>
          {useStreaming && (
            <p className="text-xs text-muted-foreground ml-6">
              生成中のテキストをリアルタイムで表示します{input.mode === 'batch' && '（バッチモードで特に効果的）'}
            </p>
          )}
        </div>

        <div>
          <Label htmlFor="creativity">創造性レベル: {input.creativityLevel || 7}</Label>
          <Slider
            id="creativity"
            value={[input.creativityLevel || 7]}
            onValueChange={(v) => onChange({ ...input, creativityLevel: v[0] })}
            min={1}
            max={10}
            step={1}
          />
        </div>

        <div>
          <Label htmlFor="seed">シード値（行動パターン制御）</Label>
          <Input
            id="seed"
            type="number"
            value={input.seed || ''}
            onChange={(e) => {
              const value = e.target.value
              if (value === '') {
                onChange({ ...input, seed: undefined })
              } else {
                onChange({ ...input, seed: parseInt(value) })
              }
            }}
            placeholder="空欄でランダム、-1で無効"
          />
          <p className="text-xs text-muted-foreground mt-1">
            {input.mode === 'standard' ? 
              'シード値により行動パターンと弱点が決まります。空欄：ランダム生成、-1：シード値無効（完全AI任せ）' :
              input.mode === 'batch' ?
              'バッチ生成時は各エネミーに異なるシード値が割り当てられます' :
              '十分な情報が提供された場合、シード値は自動的に無効化されます（情報から行動パターンを生成）'
            }
          </p>
        </div>
      </div>

      <Button
        onClick={onGenerate}
        disabled={isGenerating}
        className="w-full"
      >
        <Zap className="h-4 w-4 mr-2" />
        {isGenerating ? '生成中...' : 'エネミー生成'}
      </Button>
    </div>
  )
}